import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Card, Button, Form, Badge, Modal } from "react-bootstrap";
import { getBroadcasts, showDate, getBroadcastContacts, getBroadcastContactStatus } from "../../../helpers/api";
import Table from "../../../components/Table";
import ShreyuDatepicker from "../../../components/Datepicker";
import { Search, Eye, PencilSquare, Trash } from 'react-bootstrap-icons';
import FeatherIcon from 'feather-icons-react';

export interface Broadcast {
    br_id: string;
    broadcast_name: string;
    template_name: string;
    date: string;
    contact_count: number;
    created_at: string;
    sent_count: string,
    delivered_count: string,
    read_count: string,
    failed_count: string
    status: "scheduled" | "completed" | "failed" | "pending";
}

const BroadcastList: React.FC = () => {
    const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
    const [filtered, setFiltered] = useState<Broadcast[]>([]);
    const [loading, setLoading] = useState(true);

    const [showContactsModal, setShowContactsModal] = useState(false);
    const [contactStatusList, setContactStatusList] = useState<any[]>([]);

    const [scheduleDate, setScheduleDate] = useState<Date | undefined>(new Date());
    const [endDate, setEndDate] = useState<string>(
        new Date().toISOString().split("T")[0]   // default today
    );
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedBroadcast, setSelectedBroadcast] = useState<Broadcast | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<string | null>(null);

    const [messageCounts, setMessageCounts] = useState({
        sent: 0,
        delivered: 0,
        read: 0,
        failed: 0
    });

    useEffect(() => {
        const loadInitial = async () => {
            setLoading(true);
            const from_date = (scheduleDate ?? new Date())
                .toISOString()
                .split("T")[0];
            const to_date = endDate;

            try {
                const response = await showDate(from_date, to_date);
                const rows = response?.data?.data ?? [];
                setBroadcasts(rows);
                setFiltered(rows);
            } catch (error) {
                console.error(error);
                setFiltered([]);
            } finally {
                setLoading(false);
            }
        };

        loadInitial();
    }, []);

    const applyFilters = async () => {
        if (!scheduleDate) {
            alert("Please select start date");
            return;
        }

        const from_date = scheduleDate.toISOString().split("T")[0];
        const to_date = endDate;

        setLoading(true);

        try {
            const response = await showDate(from_date, to_date);
            const rows = response?.data?.data ?? [];

            if (rows.length === 0) {
                // alert("No broadcasts found for selected date range");
                setFiltered([]);
                return;
            }

            setFiltered(rows);
        } catch (error) {
            console.error(error);
            alert("Something went wrong while fetching broadcasts.");
            setFiltered([]);
        } finally {
            setLoading(false);
        }
    };


    const formatDate = (date: string) => {
        return new Date(date).toLocaleDateString("en-GB").replace(/\//g, "-");
    };

    const getStatusVariant = (status: Broadcast["status"]) => {
        switch (status) {
            case "completed": return "success";
            case "scheduled": return "warning";
            case "pending": return "warning";
            case "failed": return "danger";
            default: return "secondary";
        }
    };

    const handleDelete = (b: Broadcast) => {
        setSelectedBroadcast(b);
        setShowDeleteModal(true);
    };

    const handleOpenContacts = async (broadcast: Broadcast) => {
        setShowContactsModal(true);

        try {
            setLoading(true);
            const response = await getBroadcastContacts(broadcast.br_id);
            const rows = response.data?.contacts ?? [];
            setContactStatusList(rows);

            const counts = {
                sent: rows.filter((r: any) => r.message_status === "sent").length,
                delivered: rows.filter((r: any) => r.message_status === "delivered").length,
                read: rows.filter((r: any) => r.message_status === "read").length,
                failed: rows.filter((r: any) => r.message_status === "failed").length,
            };

            setMessageCounts(counts);
        } finally {
            setLoading(false);
        }
    };
    const handleOpenContactsstatus = async (
        broadcast: Broadcast,
        status?: string
    ) => {
        setShowContactsModal(true);
        setLoading(true);

        try {
            const response = await getBroadcastContactStatus(
                broadcast.br_id,
                status
            );

            setContactStatusList(response.data.contacts);

            // OPTIONAL: only if backend sends counts
            if (response.data.counts) {
                setMessageCounts({
                    sent: response.data.counts.sent ?? 0,
                    delivered: response.data.counts.delivered ?? 0,
                    read: response.data.counts.read ?? 0,
                    failed: response.data.counts.failed ?? 0,
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const filteredContacts = useMemo(() => {
        if (!selectedStatus) return contactStatusList;
        return contactStatusList.filter(
            (c: any) => c.message_status === selectedStatus
        );
    }, [contactStatusList, selectedStatus]);


    const columns = useMemo(
        () => [
            {
                Header: "S No",
                accessor: "serial_no", // FIXED
                Cell: ({ row }: any) => row.index + 1
            },
            {
                Header: "Date",
                accessor: "date",
                Cell: ({ value }: any) => formatDate(value)
            },
            {
                Header: "Broadcast Name",
                accessor: "broadcast_name"
            },
            {
                Header: "Template",
                accessor: "template_name"
            },
            {
                Header: "Contacts",
                accessor: "contact_count",
                Cell: ({ row }: any) => (
                    <Badge
                        bg="primary"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleOpenContacts(row.original)}
                    >
                        {row.original.contact_count}
                    </Badge>
                )
            },
            {
                Header: "Message Stats",
                accessor: "stats",
                Cell: ({ row }: any) => {
                    const b = row.original;
                    return (
                        <div className="mb-3 d-flex gap-2 flex-wrap">
                            <Badge
                                bg="warning"
                                style={{ cursor: "pointer" }}
                                onClick={() => handleOpenContactsstatus(row.original, "sent")}
                            >
                                Sent {b.sent_count}
                            </Badge>

                            <Badge
                                bg="info"
                                style={{ cursor: "pointer" }}
                                onClick={() => handleOpenContactsstatus(row.original, "delivered")}
                            >
                                Delivered {b.delivered_count}
                            </Badge>

                            <Badge
                                bg="success"
                                style={{ cursor: "pointer" }}
                                onClick={() => handleOpenContactsstatus(row.original, "read")}
                            >
                                Read {b.read_count}
                            </Badge>

                            <Badge
                                bg="danger"
                                style={{ cursor: "pointer" }}
                                onClick={() => handleOpenContactsstatus(row.original, "failed")}
                            >
                                Failed {b.failed_count}
                            </Badge>

                        </div>


                    );
                }
            },
            {
                Header: "Status",
                accessor: "status",
                Cell: ({ value }: any) => (
                    <Badge bg={getStatusVariant(value)}>{value}</Badge>
                )
            },
            {
                Header: "Actions",
                accessor: "actions", // FIXED
                Cell: ({ row }: any) => (
                    <Button
                        size="sm"
                        variant="danger"
                        onClick={() => handleDelete(row.original)}
                    >
                        <Trash />
                    </Button>
                )
            }
        ],
        []
    );


    return (
        <>
            <Card>
                {/* <Card.Header className="bg-secondary text-white"> */}
                {/* <h5 className="mb-0">Broadcast List</h5> */}
                {/* </Card.Header> */}

                <Card.Body>
                    <Row className="align-items-end g-3">
                        <Col md={2} sm={12}>

                        </Col>
                        {/* START DATE */}
                        <Col md={3} sm={12} >
                            <Form.Group className="me-0">
                                <Form.Label>Start Date</Form.Label>
                                <ShreyuDatepicker
                                    hideAddon
                                    dateFormat="yyyy-MM-dd"
                                    value={scheduleDate ? scheduleDate : new Date()}
                                    onChange={(date: Date) => setScheduleDate(date)}
                                />
                            </Form.Group>
                        </Col>

                        {/* END DATE */}
                        <Col md={3} sm={12} >
                            <Form.Group className="ms-0">
                                <Form.Label>End Date</Form.Label>
                                <ShreyuDatepicker
                                    hideAddon
                                    dateFormat="yyyy-MM-dd"
                                    value={new Date(endDate)}
                                    onChange={(date: Date) =>
                                        setEndDate(date.toISOString().split("T")[0])
                                    }
                                />
                            </Form.Group>
                        </Col>

                        {/* BUTTON */}
                        <Col md={2} sm={12}>
                            <Button
                                variant="primary"
                                onClick={applyFilters}
                            >
                                <i className="mdi mdi-magnify"></i> <Search />
                            </Button>
                        </Col>
                        <Col md={2} sm={12}>

                        </Col>
                    </Row>
                </Card.Body>


            </Card>

            <Card>
                <Card.Body>
                    <Badge bg="info" className="mb-3">
                        {filtered.length} broadcast(s) found
                    </Badge>

                    <Table
                        columns={columns}
                        data={filtered}
                        pageSize={10}
                        isSearchable
                        pagination
                    />
                </Card.Body>
            </Card>

            {/* CONTACT STATUS MODAL */}
            <Modal
                show={showContactsModal}
                onHide={() => setShowContactsModal(false)}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Message Status Details</Modal.Title>
                </Modal.Header>
                {/* <div className="mb-3 ms-5 d-flex gap-2">
                    <Badge bg="warning">Sent: {messageCounts.sent}</Badge>
                    <Badge bg="info">Delivered: {messageCounts.delivered}</Badge>
                    <Badge bg="success">Read: {messageCounts.read}</Badge>
                    <Badge bg="danger">Failed: {messageCounts.failed}</Badge>
                </div> */}
                <Modal.Body>

                    <Table
                        columns={[
                            {
                                Header: "S No",
                                accessor: "serial_no",
                                Cell: ({ row }: any) => row.index + 1,
                            },
                            {
                                Header: "Date",
                                accessor: "created_date_time",
                                Cell: ({ value }: { value: string }) =>
                                    value ? formatDate(value) : "",
                            },
                            {
                                Header: "Contact No",
                                accessor: "contact_no",
                            },
                            {
                                Header: "Name",
                                accessor: "contact_name",
                            },
                            {
                                Header: "Message Status",
                                accessor: "message_status",
                                Cell: ({ value }: { value: string }) => (

                                    <Badge
                                        // {value}
                                        bg={
                                            value?.toLowerCase() === "sent"
                                                ? "warning"
                                                : value === "failed"
                                                    ? "danger"
                                                    : value === "delivered"
                                                        ? "info"
                                                        : value
                                        }
                                    >
                                        {value}
                                    </Badge>
                                ),
                            },
                        ]}
                        data={contactStatusList}
                        pageSize={10}
                        isSearchable
                        pagination
                    />


                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowContactsModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default BroadcastList;
